import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { auditApi, AuditLog, AuditLogsParams } from '../lib/audit';
import { Navigate } from 'react-router-dom';
import { Filter, ArrowUpDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type SortField = 'createdAt' | 'method' | 'apiName' | 'endpoint' | 'responseStatus';
type SortDirection = 'asc' | 'desc';

const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

function CheckboxFilterGroup({
  id,
  label,
  options,
  selected,
  onChange,
}: {
  id: string;
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const selectedSet = new Set(selected);

  const toggle = (value: string, checked: boolean) => {
    const next = checked
      ? [...selected, value]
      : selected.filter((v) => v !== value);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label id={`${id}-label`}>{label}</Label>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([...options])}
          >
            All
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => onChange([])}
          >
            None
          </Button>
        </div>
      </div>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        className="rounded-md border border-input bg-background p-2 max-h-40 overflow-y-auto space-y-1"
      >
        {options.map((option) => {
          const checkboxId = `${id}-${option}`;
          return (
            <label
              key={option}
              htmlFor={checkboxId}
              className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent cursor-pointer"
            >
              <Checkbox
                id={checkboxId}
                checked={selectedSet.has(option)}
                onCheckedChange={(checked) => toggle(option, checked === true)}
              />
              <span className="font-mono text-xs">{option}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function AuditLogsPage() {
  const { user, loading: authLoading } = useAuth();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AuditLogsParams>({
    limit: 100,
    methods: [...METHOD_OPTIONS],
  });
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const isSuperUser = user?.role === 'super_user';
  const filtersAllowResults = (filters.methods?.length ?? 0) > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs', page, filters],
    queryFn: async () => {
      if (!filtersAllowResults) {
        return {
          logs: [],
          pagination: {
            page,
            limit: filters.limit ?? 100,
            total: 0,
            totalPages: 0,
          },
        };
      }
      return auditApi.getAll({ ...filters, page });
    },
    enabled: isSuperUser,
  });

  const sortedLogs = useMemo(() => {
    if (!data?.logs) return [];
    const items = [...data.logs];
    items.sort((a: AuditLog, b: AuditLog) => {
      let aVal = '';
      let bVal = '';

      if (sortField === 'createdAt') {
        aVal = a.createdAt;
        bVal = b.createdAt;
      } else if (sortField === 'method') {
        aVal = a.method;
        bVal = b.method;
      } else if (sortField === 'apiName') {
        aVal = a.apiName;
        bVal = b.apiName;
      } else if (sortField === 'endpoint') {
        aVal = a.endpoint;
        bVal = b.endpoint;
      } else if (sortField === 'responseStatus') {
        aVal = a.responseStatus ?? '';
        bVal = b.responseStatus ?? '';
      }

      if (!aVal && !bVal) return 0;
      if (!aVal) return sortDirection === 'asc' ? -1 : 1;
      if (!bVal) return sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'createdAt') {
        const aTime = new Date(aVal).getTime();
        const bTime = new Date(bVal).getTime();
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data?.logs, sortField, sortDirection]);

  if (authLoading) {
    return (
      <div className="px-2 py-3 sm:px-0">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSuperUser) {
    return <Navigate to="/" replace />;
  }

  const formatDateTime = (value?: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getMethodVariant = (method: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'secondary';
      case 'POST':
        return 'default';
      case 'PUT':
      case 'PATCH':
        return 'outline';
      case 'DELETE':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (!status) return 'outline';
    const code = parseInt(status, 10);
    if (code >= 200 && code < 300) return 'default';
    if (code >= 300 && code < 400) return 'secondary';
    if (code >= 400 && code < 500) return 'outline';
    if (code >= 500) return 'destructive';
    return 'outline';
  };

  return (
    <div className="py-3">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-heading">Audit Logs</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          View API requests and system activity recorded in the audit log
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="audit-filter-start-date">Start Date</Label>
              <Input
                type="datetime-local"
                id="audit-filter-start-date"
                value={filters.startDate || ''}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value || undefined })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="audit-filter-end-date">End Date</Label>
              <Input
                type="datetime-local"
                id="audit-filter-end-date"
                value={filters.endDate || ''}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value || undefined })
                }
              />
            </div>
            <CheckboxFilterGroup
              id="audit-filter-methods"
              label="Methods"
              options={METHOD_OPTIONS}
              selected={filters.methods ?? []}
              onChange={(methods) => setFilters((prev) => ({ ...prev, methods }))}
            />
            <div className="space-y-2">
              <Label htmlFor="audit-filter-api-name">API Name</Label>
              <Input
                type="text"
                id="audit-filter-api-name"
                placeholder="Search API name..."
                value={filters.apiName || ''}
                onChange={(e) =>
                  setFilters({ ...filters, apiName: e.target.value || undefined })
                }
              />
            </div>
            <div className="space-y-2 sm:col-span-2 lg:col-span-4">
              <Label htmlFor="audit-filter-search">Search endpoint</Label>
              <Input
                type="text"
                id="audit-filter-search"
                placeholder="Search in endpoint..."
                value={filters.search || ''}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value || undefined })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card className="text-center py-12">
          <p className="text-muted-foreground">Loading audit logs...</p>
        </Card>
      ) : error ? (
        <Card className="text-center py-12">
          <p className="text-destructive">Error loading audit logs</p>
        </Card>
      ) : !data || data.logs.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-muted-foreground">No audit logs found</p>
        </Card>
      ) : (
        <>
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead
                      className="px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                      onClick={() => toggleSort('createdAt')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Timestamp
                        <ArrowUpDown className="h-3 w-3" />
                      </span>
                    </TableHead>
                    <TableHead
                      className="px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                      onClick={() => toggleSort('method')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Method
                        <ArrowUpDown className="h-3 w-3" />
                      </span>
                    </TableHead>
                    <TableHead
                      className="px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                      onClick={() => toggleSort('apiName')}
                    >
                      <span className="inline-flex items-center gap-1">
                        API Name
                        <ArrowUpDown className="h-3 w-3" />
                      </span>
                    </TableHead>
                    <TableHead
                      className="hidden lg:table-cell px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                      onClick={() => toggleSort('endpoint')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Endpoint
                        <ArrowUpDown className="h-3 w-3" />
                      </span>
                    </TableHead>
                    <TableHead className="hidden md:table-cell px-3 sm:px-4 md:px-6">
                      User
                    </TableHead>
                    <TableHead className="hidden xl:table-cell px-3 sm:px-4 md:px-6">
                      IP
                    </TableHead>
                    <TableHead className="hidden xl:table-cell px-3 sm:px-4 md:px-6">
                      DB
                    </TableHead>
                    <TableHead
                      className="px-3 sm:px-4 md:px-6 cursor-pointer select-none"
                      onClick={() => toggleSort('responseStatus')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Status
                        <ArrowUpDown className="h-3 w-3" />
                      </span>
                    </TableHead>
                    <TableHead className="hidden 2xl:table-cell px-3 sm:px-4 md:px-6">
                      Request Body
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap">
                        {formatDateTime(log.createdAt)}
                      </TableCell>
                      <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap">
                        <Badge variant={getMethodVariant(log.method)}>{log.method}</Badge>
                      </TableCell>
                      <TableCell className="px-3 sm:px-4 md:px-6">
                        <div className="truncate" title={log.apiName}>
                          {log.apiName}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell px-3 sm:px-4 md:px-6 font-mono text-xs">
                        <div className="truncate max-w-xs" title={log.endpoint}>
                          {log.endpoint}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell px-3 sm:px-4 md:px-6">
                        <div
                          className="truncate max-w-[12rem]"
                          title={log.userEmail || log.userName || undefined}
                        >
                          {log.userEmail || log.userName || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden xl:table-cell px-3 sm:px-4 md:px-6 whitespace-nowrap">
                        {log.ipAddress || '—'}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell px-3 sm:px-4 md:px-6 font-mono text-xs">
                        {log.dbTable
                          ? `${log.dbTable}${log.dbRowId ? `:${log.dbRowId}` : ''}`
                          : '—'}
                      </TableCell>
                      <TableCell className="px-3 sm:px-4 md:px-6 whitespace-nowrap">
                        {log.responseStatus ? (
                          <Badge variant={getStatusVariant(log.responseStatus)}>
                            {log.responseStatus}
                          </Badge>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell px-3 sm:px-4 md:px-6 max-w-md">
                        <div className="font-mono text-xs whitespace-pre-wrap break-words line-clamp-4">
                          {log.requestBody ?? '—'}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {data.pagination && data.pagination.totalPages > 1 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing page {data.pagination.page} of {data.pagination.totalPages} (
                {data.pagination.total} total logs)
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
